import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { html, css } from 'lit';
import { customElement } from 'lit/decorators.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import type { LPTranslationDictionary } from 'db/translations.js';

const OWL_LOGO = require('./assets/OwlTransparent.svg');

@customElement('compliance-page')
export class CompliancePage extends ChemistryLitElement {
  
  // WICHTIG: Auch hier den Safari-Block-Bug fixen!
  static override styles = css`
    :host {
      display: block;
      width: 100%;
    }
  `;

  t = new TranslationController<LPTranslationDictionary>(this);

  // --- BUGFIX: Adornis Scroll/Resize Fix ---
  private __stylesAlreadyApplied = false;

  override async _updateStyles() {
    if (this.__stylesAlreadyApplied) {
      return;
    }
    await super._updateStyles();
    this.__stylesAlreadyApplied = true;
  }
  // -----------------------------------------

  override render() {
    return html`
      <div class="bg-slate-50 min-h-[100svh] flex flex-col text-gray-900 font-inter">
        
        <nav class="w-full px-6 py-4 flex justify-between items-center bg-white shadow-sm border-b border-gray-100 flex-shrink-0">
          <div class="flex container mx-auto items-center">
            <a href="/" class="flex items-center space-x-3 cursor-pointer hover:opacity-80 transition-opacity">
              <img src="${OWL_LOGO}" alt="Gradias Owl" class="w-8 h-8 object-contain" />
              <span class="text-2xl font-poppins font-extrabold tracking-wide text-[#89B4D6]">gradias</span>
            </a>
          </div>
        </nav>

        <main class="flex-1 container mx-auto px-6 py-12 max-w-4xl">
          <a href="/" class="inline-flex items-center text-[#5594CF] hover:text-[#3d7ab6] font-semibold mb-8 text-sm transition-colors">
            &larr; ${this.t.translate('compliancePage', 'backToHome')}
          </a>
          
          <div class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-12 mb-12">
            <header class="mb-12 border-b border-gray-100 pb-8">
              <h1 class="text-3xl md:text-4xl font-extrabold text-slate-800 mb-4 leading-tight">${this.t.translate('compliancePage', 'headerTitle')}</h1>
              <p class="text-lg md:text-xl text-gray-500 mb-6">${this.t.translate('compliancePage', 'headerSubtitle')}</p>
              <div class="text-sm text-gray-400 font-medium tracking-wide uppercase">${this.t.translate('compliancePage', 'lastUpdated')}</div>
            </header>

            <div class="prose prose-slate prose-blue max-w-none prose-headings:font-bold prose-headings:text-slate-800 prose-a:text-[#5594CF] prose-li:marker:text-[#5594CF]">
              
              <h2 class="text-2xl mt-10 mb-4 pb-2 border-b border-gray-100">${this.t.translate('compliancePage', 'section1Title')}</h2>
              <p>${this.t.translate('compliancePage', 'section1Text1')}</p>
              <p>${this.t.translate('compliancePage', 'section1Text2')}</p>
              <div class="bg-blue-50/50 border border-blue-100 rounded-xl p-6 my-8">
                <h3 class="text-lg text-[#5594CF] mt-0 mb-4">${this.t.translate('compliancePage', 'guaranteesTitle')}</h3>
                <ul class="mt-0 mb-0 space-y-2">
                  <li><strong>${this.t.translate('compliancePage', 'guarantee1')}</strong></li>
                  <li><strong>${this.t.translate('compliancePage', 'guarantee2')}</strong></li>
                  <li><strong>${this.t.translate('compliancePage', 'guarantee3')}</strong></li>
                </ul>
              </div>

              <h2 class="text-2xl mt-12 mb-4 pb-2 border-b border-gray-100">${this.t.translate('compliancePage', 'section2Title')}</h2>
              
              <h3 class="text-xl mt-6 mb-3">${this.t.translate('compliancePage', 'sec2_1Title')}</h3>
              <p>${this.t.translate('compliancePage', 'sec2_1Text')}</p>
              
              <div class="overflow-x-auto my-6 rounded-lg border border-gray-200 shadow-inner">
                <table class="w-full text-sm text-left text-gray-600 min-w-[600px]"> <thead class="text-xs text-gray-700 uppercase bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th class="px-6 py-4 w-1/3">${this.t.translate('compliancePage', 'roleTableCol1')}</th>
                      <th class="px-6 py-4">${this.t.translate('compliancePage', 'roleTableCol2')}</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100">
                    <tr class="bg-white"><td class="px-6 py-4 font-semibold text-slate-800">${this.t.translate('compliancePage', 'roleController')}</td><td class="px-6 py-4">${this.t.translate('compliancePage', 'roleControllerDesc')}</td></tr>
                    <tr class="bg-gray-50/50"><td class="px-6 py-4 font-semibold text-slate-800">${this.t.translate('compliancePage', 'roleProcessor')}</td><td class="px-6 py-4">${this.t.translate('compliancePage', 'roleProcessorDesc')}</td></tr>
                    <tr class="bg-white"><td class="px-6 py-4 font-semibold text-slate-800">${this.t.translate('compliancePage', 'roleSubProcessor')}</td><td class="px-6 py-4">${this.t.translate('compliancePage', 'roleSubProcessorDesc')}</td></tr>
                  </tbody>
                </table>
              </div>

              <h3 class="text-xl mt-8 mb-3">${this.t.translate('compliancePage', 'sec2_2Title')}</h3>
              <p>${this.t.translate('compliancePage', 'sec2_2Text')}</p>
              <ul class="space-y-2">
                <li>${this.t.translate('compliancePage', 'legalBasis1')}</li>
                <li>${this.t.translate('compliancePage', 'legalBasis2')}</li>
                <li>${this.t.translate('compliancePage', 'legalBasis3')}</li>
              </ul>
              <p>${this.t.translate('compliancePage', 'sec2_2Footer')}</p>

              <h3 class="text-xl mt-8 mb-3">${this.t.translate('compliancePage', 'sec2_3Title')}</h3>
              
              <div class="overflow-x-auto my-6 rounded-lg border border-gray-200 shadow-inner">
                <table class="w-full text-sm text-left text-gray-600 min-w-[700px]"> <thead class="text-xs text-gray-700 uppercase bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th class="px-6 py-4 whitespace-nowrap">${this.t.translate('compliancePage', 'dataTableCol1')}</th>
                      <th class="px-6 py-4">${this.t.translate('compliancePage', 'dataTableCol2')}</th>
                      <th class="px-6 py-4 whitespace-nowrap">${this.t.translate('compliancePage', 'dataTableCol3')}</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100">
                    <tr class="bg-white"><td class="px-6 py-4 font-medium text-slate-800">${this.t.translate('compliancePage', 'dataCat1Title')}</td><td class="px-6 py-4">${this.t.translate('compliancePage', 'dataCat1Ex')}</td><td class="px-6 py-4 text-green-600 font-medium">${this.t.translate('compliancePage', 'dataCat1Status')}</td></tr>
                    <tr class="bg-gray-50/50"><td class="px-6 py-4 font-medium text-slate-800">${this.t.translate('compliancePage', 'dataCat2Title')}</td><td class="px-6 py-4">${this.t.translate('compliancePage', 'dataCat2Ex')}</td><td class="px-6 py-4 text-[#5594CF] font-medium">${this.t.translate('compliancePage', 'dataCat2Status')}</td></tr>
                    <tr class="bg-white"><td class="px-6 py-4 font-medium text-slate-800">${this.t.translate('compliancePage', 'dataCat3Title')}</td><td class="px-6 py-4">${this.t.translate('compliancePage', 'dataCat3Ex')}</td><td class="px-6 py-4 text-green-600 font-medium">${this.t.translate('compliancePage', 'dataCat3Status')}</td></tr>
                    <tr class="bg-gray-50/50"><td class="px-6 py-4 font-medium text-slate-800">${this.t.translate('compliancePage', 'dataCat4Title')}</td><td class="px-6 py-4">${this.t.translate('compliancePage', 'dataCat4Ex')}</td><td class="px-6 py-4 text-green-600 font-medium">${this.t.translate('compliancePage', 'dataCat4Status')}</td></tr>
                    <tr class="bg-white"><td class="px-6 py-4 font-medium text-slate-800">${this.t.translate('compliancePage', 'dataCat5Title')}</td><td class="px-6 py-4">${this.t.translate('compliancePage', 'dataCat5Ex')}</td><td class="px-6 py-4 text-red-500 font-medium">${this.t.translate('compliancePage', 'dataCat5Status')}</td></tr>
                    <tr class="bg-gray-50/50"><td class="px-6 py-4 font-medium text-slate-800">${this.t.translate('compliancePage', 'dataCat6Title')}</td><td class="px-6 py-4">${this.t.translate('compliancePage', 'dataCat6Ex')}</td><td class="px-6 py-4 text-red-500 font-medium">${this.t.translate('compliancePage', 'dataCat6Status')}</td></tr>
                    <tr class="bg-white"><td class="px-6 py-4 font-medium text-slate-800">${this.t.translate('compliancePage', 'dataCat7Title')}</td><td class="px-6 py-4">${this.t.translate('compliancePage', 'dataCat7Ex')}</td><td class="px-6 py-4 text-red-500 font-medium">${this.t.translate('compliancePage', 'dataCat7Status')}</td></tr>
                  </tbody>
                </table>
              </div>
              <p><strong>${this.t.translate('compliancePage', 'sec2_3Footer')}</strong></p>

              <h3 class="text-xl mt-8 mb-3">${this.t.translate('compliancePage', 'sec2_4Title')}</h3>
              <p>${this.t.translate('compliancePage', 'sec2_4Text')}</p>
              <ul class="space-y-2">
                <li>${this.t.translate('compliancePage', 'hosting1')}</li>
                <li>${this.t.translate('compliancePage', 'hosting2')}</li>
                <li>${this.t.translate('compliancePage', 'hosting3')}</li>
                <li>${this.t.translate('compliancePage', 'hosting4')}</li>
                <li>${this.t.translate('compliancePage', 'hosting5')}</li>
              </ul>

              <h3 class="text-xl mt-8 mb-3">${this.t.translate('compliancePage', 'sec2_5Title')}</h3>
              <p>${this.t.translate('compliancePage', 'sec2_5Text')}</p>
              <ul class="space-y-2">
                <li>${this.t.translate('compliancePage', 'ai1')}</li>
                <li>${this.t.translate('compliancePage', 'ai2')}</li>
                <li>${this.t.translate('compliancePage', 'ai3')}</li>
                <li>${this.t.translate('compliancePage', 'ai4')}</li>
                <li>${this.t.translate('compliancePage', 'ai5')}</li>
              </ul>

              <h3 class="text-xl mt-8 mb-3">${this.t.translate('compliancePage', 'sec2_6Title')}</h3>
              <p>${this.t.translate('compliancePage', 'sec2_6Text')}</p>
              <ol class="space-y-2 list-decimal ml-5 mb-6">
                <li>${this.t.translate('compliancePage', 'flow1')}</li>
                <li>${this.t.translate('compliancePage', 'flow2')}</li>
                <li>${this.t.translate('compliancePage', 'flow3')}</li>
                <li>${this.t.translate('compliancePage', 'flow4')}</li>
                <li>${this.t.translate('compliancePage', 'flow5')}</li>
                <li><strong>${this.t.translate('compliancePage', 'flow6')}</strong></li>
                <li>${this.t.translate('compliancePage', 'flow7')}</li>
              </ol>

              <h3 class="text-xl mt-8 mb-3">${this.t.translate('compliancePage', 'sec2_7Title')}</h3>
              <ul class="space-y-2">
                <li>${this.t.translate('compliancePage', 'storage1')}</li>
                <li>${this.t.translate('compliancePage', 'storage2')}</li>
                <li>${this.t.translate('compliancePage', 'storage3')}</li>
                <li>${this.t.translate('compliancePage', 'storage4')}</li>
                <li>${this.t.translate('compliancePage', 'storage5')}</li>
              </ul>

              <h3 class="text-xl mt-8 mb-3">${this.t.translate('compliancePage', 'sec2_8Title')}</h3>
              <p>${this.t.translate('compliancePage', 'sec2_8Text')}</p>
              <ul class="space-y-2">
                <li>${this.t.translate('compliancePage', 'rights1')}</li>
                <li>${this.t.translate('compliancePage', 'rights2')}</li>
                <li>${this.t.translate('compliancePage', 'rights3')}</li>
                <li>${this.t.translate('compliancePage', 'rights4')}</li>
                <li>${this.t.translate('compliancePage', 'rights5')}</li>
                <li>${this.t.translate('compliancePage', 'rights6')}</li>
              </ul>

              <h3 class="text-xl mt-8 mb-3">${this.t.translate('compliancePage', 'sec2_9Title')}</h3>
              <ul class="space-y-2">
                <li>${this.t.translate('compliancePage', 'tom1')}</li>
                <li>${this.t.translate('compliancePage', 'tom2')}</li>
                <li>${this.t.translate('compliancePage', 'tom3')}</li>
                <li>${this.t.translate('compliancePage', 'tom4')}</li>
                <li>${this.t.translate('compliancePage', 'tom5')}</li>
              </ul>

              <h3 class="text-xl mt-8 mb-3">${this.t.translate('compliancePage', 'sec2_10Title')}</h3>
              
              <div class="overflow-x-auto my-6 rounded-lg border border-gray-200 shadow-inner">
                <table class="w-full text-sm text-left text-gray-600 min-w-[800px]"> <thead class="text-xs text-gray-700 uppercase bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th class="px-6 py-4 whitespace-nowrap">${this.t.translate('compliancePage', 'subTableCol1')}</th>
                      <th class="px-6 py-4">${this.t.translate('compliancePage', 'subTableCol2')}</th>
                      <th class="px-6 py-4">${this.t.translate('compliancePage', 'subTableCol3')}</th>
                      <th class="px-6 py-4">${this.t.translate('compliancePage', 'subTableCol4')}</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100">
                    <tr class="bg-white">
                      <td class="px-6 py-4 font-semibold text-slate-800">${this.t.translate('compliancePage', 'sub1Name')}</td>
                      <td class="px-6 py-4">${this.t.translate('compliancePage', 'sub1Purpose')}</td>
                      <td class="px-6 py-4">${this.t.translate('compliancePage', 'sub1Location')}</td>
                      <td class="px-6 py-4">${this.t.translate('compliancePage', 'sub1Contract')}</td>
                    </tr>
                    <tr class="bg-gray-50/50">
                      <td class="px-6 py-4 font-semibold text-slate-800">${this.t.translate('compliancePage', 'sub2Name')}</td>
                      <td class="px-6 py-4">${this.t.translate('compliancePage', 'sub2Purpose')}</td>
                      <td class="px-6 py-4">${this.t.translate('compliancePage', 'sub2Location')}</td>
                      <td class="px-6 py-4">${this.t.translate('compliancePage', 'sub2Contract')}</td>
                    </tr>
                    <tr class="bg-white">
                      <td class="px-6 py-4 font-semibold text-slate-800">${this.t.translate('compliancePage', 'sub3Name')}</td>
                      <td class="px-6 py-4">${this.t.translate('compliancePage', 'sub3Purpose')}</td>
                      <td class="px-6 py-4">${this.t.translate('compliancePage', 'sub3Location')}</td>
                      <td class="px-6 py-4">${this.t.translate('compliancePage', 'sub3Contract')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p>${this.t.translate('compliancePage', 'sec2_10Footer')}</p>

              <h2 class="text-2xl mt-12 mb-4 pb-2 border-b border-gray-100">${this.t.translate('compliancePage', 'section3Title')}</h2>
              
              <h3 class="text-xl mt-6 mb-3">${this.t.translate('compliancePage', 'sec3_1Title')}</h3>
              <p>${this.t.translate('compliancePage', 'sec3_1Text')}</p>

              <h3 class="text-xl mt-8 mb-3">${this.t.translate('compliancePage', 'sec3_2Title')}</h3>
              
              <div class="overflow-x-auto my-6 rounded-lg border border-gray-200 shadow-inner">
                <table class="w-full text-sm text-left text-gray-600 min-w-[600px]">
                  <thead class="text-xs text-gray-700 uppercase bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th class="px-6 py-4 w-1/4 whitespace-nowrap">${this.t.translate('compliancePage', 'provTableCol1')}</th>
                      <th class="px-6 py-4">${this.t.translate('compliancePage', 'provTableCol2')}</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100">
                    <tr class="bg-white"><td class="px-6 py-4 font-semibold text-slate-800">${this.t.translate('compliancePage', 'prov1Name')}</td><td class="px-6 py-4">${this.t.translate('compliancePage', 'prov1Desc')}</td></tr>
                    <tr class="bg-gray-50/50"><td class="px-6 py-4 font-semibold text-slate-800">${this.t.translate('compliancePage', 'prov2Name')}</td><td class="px-6 py-4">${this.t.translate('compliancePage', 'prov2Desc')}</td></tr>
                    <tr class="bg-white"><td class="px-6 py-4 font-semibold text-slate-800">${this.t.translate('compliancePage', 'prov3Name')}</td><td class="px-6 py-4">${this.t.translate('compliancePage', 'prov3Desc')}</td></tr>
                    <tr class="bg-gray-50/50"><td class="px-6 py-4 font-semibold text-slate-800">${this.t.translate('compliancePage', 'prov4Name')}</td><td class="px-6 py-4">${this.t.translate('compliancePage', 'prov4Desc')}</td></tr>
                    <tr class="bg-white"><td class="px-6 py-4 font-semibold text-slate-800">${this.t.translate('compliancePage', 'prov5Name')}</td><td class="px-6 py-4">${this.t.translate('compliancePage', 'prov5Desc')}</td></tr>
                    <tr class="bg-gray-50/50"><td class="px-6 py-4 font-semibold text-slate-800">${this.t.translate('compliancePage', 'prov6Name')}</td><td class="px-6 py-4">${this.t.translate('compliancePage', 'prov6Desc')}</td></tr>
                    <tr class="bg-white"><td class="px-6 py-4 font-semibold text-slate-800">${this.t.translate('compliancePage', 'prov7Name')}</td><td class="px-6 py-4">${this.t.translate('compliancePage', 'prov7Desc')}</td></tr>
                    <tr class="bg-gray-50/50"><td class="px-6 py-4 font-semibold text-slate-800">${this.t.translate('compliancePage', 'prov8Name')}</td><td class="px-6 py-4">${this.t.translate('compliancePage', 'prov8Desc')}</td></tr>
                  </tbody>
                </table>
              </div>

              <h3 class="text-xl mt-8 mb-3">${this.t.translate('compliancePage', 'sec3_3Title')}</h3>
              <p>${this.t.translate('compliancePage', 'sec3_3Text')}</p>
              <ul class="space-y-2">
                <li>${this.t.translate('compliancePage', 'dep1')}</li>
                <li>${this.t.translate('compliancePage', 'dep2')}</li>
                <li>${this.t.translate('compliancePage', 'dep3')}</li>
                <li>${this.t.translate('compliancePage', 'dep4')}</li>
              </ul>

              <h3 class="text-xl mt-8 mb-3">${this.t.translate('compliancePage', 'sec3_4Title')}</h3>
              <p>${this.t.translate('compliancePage', 'sec3_4Text')}</p>
              <ul class="space-y-2">
                <li>${this.t.translate('compliancePage', 'lit1')}</li>
                <li>${this.t.translate('compliancePage', 'lit2')}</li>
                <li>${this.t.translate('compliancePage', 'lit3')}</li>
              </ul>

              <h3 class="text-xl mt-8 mb-3">${this.t.translate('compliancePage', 'sec3_5Title')}</h3>
              <p>${this.t.translate('compliancePage', 'sec3_5Text')}</p>
              <ul class="space-y-2">
                <li>${this.t.translate('compliancePage', 'prac1')}</li>
                <li>${this.t.translate('compliancePage', 'prac2')}</li>
                <li>${this.t.translate('compliancePage', 'prac3')}</li>
                <li>${this.t.translate('compliancePage', 'prac4')}</li>
              </ul>

              <h2 class="text-2xl mt-12 mb-4 pb-2 border-b border-gray-100">${this.t.translate('compliancePage', 'section4Title')}</h2>
              <ul class="space-y-2">
                <li>${this.t.translate('compliancePage', 'contact1')}</li>
                <li>${this.t.translate('compliancePage', 'contact2')}</li>
                <li>${this.t.translate('compliancePage', 'contact3')}</li>
                <li>${this.t.translate('compliancePage', 'contact4')}</li>
              </ul>

              <h2 class="text-2xl mt-12 mb-4 pb-2 border-b border-gray-100">${this.t.translate('compliancePage', 'section5Title')}</h2>
              <p>${this.t.translate('compliancePage', 'sec5Text')}</p>
              <ul class="grid grid-cols-1 md:grid-cols-2 gap-4 list-none pl-0 mt-4 mb-8">
                <li class="bg-gray-50 p-3 rounded flex items-start gap-3"><div class="text-[#5594CF] mt-0.5">✓</div> ${this.t.translate('compliancePage', 'doc1')}</li>
                <li class="bg-gray-50 p-3 rounded flex items-start gap-3"><div class="text-[#5594CF] mt-0.5">✓</div> ${this.t.translate('compliancePage', 'doc2')}</li>
                <li class="bg-gray-50 p-3 rounded flex items-start gap-3"><div class="text-[#5594CF] mt-0.5">✓</div> ${this.t.translate('compliancePage', 'doc3')}</li>
                <li class="bg-gray-50 p-3 rounded flex items-start gap-3"><div class="text-[#5594CF] mt-0.5">✓</div> ${this.t.translate('compliancePage', 'doc4')}</li>
                <li class="bg-gray-50 p-3 rounded flex items-start gap-3"><div class="text-[#5594CF] mt-0.5">✓</div> ${this.t.translate('compliancePage', 'doc5')}</li>
                <li class="bg-gray-50 p-3 rounded flex items-start gap-3"><div class="text-[#5594CF] mt-0.5">✓</div> ${this.t.translate('compliancePage', 'doc6')}</li>
                <li class="bg-gray-50 p-3 rounded flex items-start gap-3"><div class="text-[#5594CF] mt-0.5">✓</div> ${this.t.translate('compliancePage', 'doc7')}</li>
                <li class="bg-gray-50 p-3 rounded flex items-start gap-3"><div class="text-[#5594CF] mt-0.5">✓</div> ${this.t.translate('compliancePage', 'doc8')}</li>
              </ul>

            </div>
          </div>
        </main>
        
        <footer class="bg-slate-900 py-12 text-center text-sm text-slate-400 flex-shrink-0 w-full">
          <div class="mx-auto px-6">
            <p>${this.t.translate('compliancePage', 'footerText1')}</p>
            <p class="mt-2 text-xs text-slate-500 max-w-2xl mx-auto">${this.t.translate('compliancePage', 'footerText2')}</p>
          </div>
        </footer>
      </div>
    `;
  }
}